// @ts-nocheck
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { CartAddEvent } from '@theme/events';

/**
 * Fetches /cart.js using the locale-correct URL (avoids a 302 redirect on
 * locale-prefixed storefronts) and safely parses the JSON body.
 * On any failure, warns loudly and returns a usable fallback cart object so
 * awaiting callers don't crash.
 *
 * @returns {Promise<object>} The cart object, or a fallback cart shape.
 */
async function fetchCartJSON() {
  const fallback = window.Theme?.globalVariables?.cart || { items: [], item_count: 0 };
  try {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!response.ok) {
      console.warn(`fetchCartJSON: /cart.js responded with status ${response.status}`);
      return fallback;
    }
    const body = await response.text();
    if (!body || !body.trim()) {
      console.warn('fetchCartJSON: /cart.js returned an empty body');
      return fallback;
    }
    return JSON.parse(body);
  } catch (error) {
    console.warn('fetchCartJSON: failed to fetch or parse /cart.js', error);
    return fallback;
  }
}

/**
 * Refreshes the cart state globally — header count, cart drawer, and cart page.
 * For logged-out users, also removes redeemed products before refreshing.
 * Call from anywhere: window.refreshCart()
 *
 * @param {{ seed?: boolean }} [options] - When `seed` is true, the first cart
 *   read comes from the server-rendered Theme.globalVariables.cart instead of
 *   a /cart.js fetch. Used for the initial page-load call.
 * @returns {Promise<object>} The updated cart object.
 */
async function refreshCart({ seed = false } = {}) {
  // On initial cart-page load the cart is already server-rendered into
  // Theme.globalVariables.cart; seed from it instead of fetching /cart.js.
  let cart = seed
    ? window.Theme?.globalVariables?.cart || (await fetchCartJSON())
    : await fetchCartJSON();

  if (!window.customer) {
    const hasRedeemed = cart.items.some(item =>
      item.properties && (item.properties._redeemed_product === 'true' || item.properties._redeemed_product === true)
    );

    if (hasRedeemed) {
      const updates = {};
      cart.items.forEach(item => {
        if (item.properties && (item.properties._redeemed_product === 'true' || item.properties._redeemed_product === true)) {
          updates[item.key] = 0;
        }
      });

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ updates })
      });

      cart = await fetchCartJSON();
    } else if (seed) {
      // Seeded from the freshly server-rendered cart with nothing to strip:
      // the header count, cart drawer, and cart section already match this
      // cart, so skip the redundant section morphs and event dispatch.
      return cart;
    }
  }

  if (window.Theme?.globalVariables) window.Theme.globalVariables.cart = cart;

  document.querySelectorAll('[data-cart-item-count]').forEach(el => {
    el.textContent = String(cart.item_count);
    el.classList.toggle('hidden', cart.item_count === 0);
  });

  const cartDrawer = document.getElementById('shopify-section-cart-drawer');
  const drawerSectionId = cartDrawer?.dataset?.sectionId;
  if (drawerSectionId) {
    const drawerHtml = await fetch(`${window.Shopify?.routes?.root || '/'}?section_id=${drawerSectionId}`, { cache: 'no-store' }).then(r => r.text());
    morphSection('cart-drawer', drawerHtml);
  }

  const cartPage = document.querySelector('cart-items-component');
  if (cartPage) {
    const cartSectionId = cartPage.dataset.sectionId;
    if (typeof window.replaceCartSection === 'function') {
      const html = await sectionRenderer.getSectionHTML(cartSectionId, false);
      window.replaceCartSection(cartSectionId, html);
    } else {
      await sectionRenderer.renderSection(cartSectionId, { cache: false });
    }
    const freshCartPage = document.querySelector('cart-items-component');
    if (freshCartPage && typeof freshCartPage.calculateTotalLoyaltyPoints === 'function') {
      freshCartPage.calculateTotalLoyaltyPoints();
    }
  }

  document.dispatchEvent(
    new CartAddEvent(cart, 'refresh', {
      source: 'refreshCart',
      itemCount: cart.item_count,
    })
  );

  return cart;
}

/**
 * Fetches fresh cart data from /cart.js and updates Theme.globalVariables.cart.
 * Lightweight alternative to refreshCart — no section replacements or events.
 * Call from anywhere: window.syncCartState()
 *
 * @returns {Promise<object>} The updated cart object.
 */
async function syncCartState() {
  const cart = await fetchCartJSON();
  if (Theme?.globalVariables) Theme.globalVariables.cart = cart;
  return cart;
}

window.refreshCart = refreshCart;
window.syncCartState = syncCartState;
if (!window.customer) refreshCart({ seed: true });
